/**
 * 日期与时间格式（PRD §8.4「格式」；spec-core §1.9 I6；platform REQ-8.4-003）：
 * - 日期：中 `2026-11-01`、韩 `2026. 11. 1.`、日 `2026/11/01`、英 `Nov 1, 2026`；时间一律 24 小时制 `14:02`；
 * - 时间一律按设备时区显示（timeZone 不传即设备时区）；后台按 UTC+9 显示时由调用方传 timeZone；
 * - 规则中的平台日自动换算：平台日起点（UTC 的 'HH:MM' 或偏移分钟）换成设备本地时刻，例如 UTC+8 用户看到"每天 23:00 重置"。
 * 数字千分位见 NumberSymbolsProvider / formatDecimal（@nexora/shared），这里只处理日期与时间。
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Lang } from '@nexora/shared';

const LOCALE: Readonly<Record<Lang, string>> = { zh: 'zh-CN', ko: 'ko-KR', ja: 'ja-JP', en: 'en-US' };

export type DateValue = Date | number | string;

export interface DateFormatOptions {
  /** IANA 时区；不传为设备时区 */
  timeZone?: string | undefined;
  /** 时间带秒（单据详情等需要秒的地方） */
  seconds?: boolean | undefined;
}

function toDate(value: DateValue): Date {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) throw new RangeError(`Invalid date: ${String(value)}`);
  return date;
}

function part(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return parts.find((p) => p.type === type)?.value ?? '';
}

/** 日期（spec-core I6）。 */
export function formatDate(lang: Lang, value: DateValue, options: DateFormatOptions = {}): string {
  const date = toDate(value);
  const tz = options.timeZone ? { timeZone: options.timeZone } : {};
  switch (lang) {
    case 'zh': {
      const parts = new Intl.DateTimeFormat('en-US', { ...tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
      return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')}`;
    }
    case 'ko':
      return new Intl.DateTimeFormat(LOCALE.ko, { ...tz, year: 'numeric', month: 'numeric', day: 'numeric' }).format(date);
    case 'ja':
      return new Intl.DateTimeFormat(LOCALE.ja, { ...tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
    default:
      return new Intl.DateTimeFormat(LOCALE.en, { ...tz, year: 'numeric', month: 'short', day: 'numeric' }).format(date);
  }
}

/** 时刻：24 小时制 HH:mm（可带秒），四种语言相同（spec-core I6）。 */
export function formatTime(value: DateValue, options: DateFormatOptions = {}): string {
  const date = toDate(value);
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(options.timeZone ? { timeZone: options.timeZone } : {}),
    hour: '2-digit',
    minute: '2-digit',
    ...(options.seconds ? { second: '2-digit' } : {}),
    hourCycle: 'h23',
  }).formatToParts(date);
  const hh = part(parts, 'hour');
  const text = `${hh === '24' ? '00' : hh}:${part(parts, 'minute')}`;
  return options.seconds ? `${text}:${part(parts, 'second')}` : text;
}

/** 日期 + 时刻（PRD §1.4 P5"所有时间写到具体的日期和时刻"）。 */
export function formatDateTime(lang: Lang, value: DateValue, options: DateFormatOptions = {}): string {
  return `${formatDate(lang, value, options)} ${formatTime(value, options)}`;
}

function dayKey(value: Date, timeZone: string | undefined): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    ...(timeZone ? { timeZone } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  return `${part(parts, 'year')}-${part(parts, 'month')}-${part(parts, 'day')}`;
}

/**
 * 预计时刻：与 now 同一天只显示 `HH:mm`，跨日显示日期 + 时刻（"维护中 · 预计 03:30 恢复"等，platform C.5）。
 */
export function formatClock(lang: Lang, value: DateValue, now: DateValue = Date.now(), options: DateFormatOptions = {}): string {
  const date = toDate(value);
  return dayKey(date, options.timeZone) === dayKey(toDate(now), options.timeZone)
    ? formatTime(date, options)
    : formatDateTime(lang, date, options);
}

const HHMM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * UTC 的每日时刻 'HH:MM'（例如身份接口返回的 platformDayStartUtc）→ 设备本地时刻 'HH:mm'。
 * 例：平台日以 UTC+9 00:00 为界 = UTC 15:00；UTC+8 的设备显示"23:00"（PRD §8.4）。
 * on 用于确定换算当天（夏令时地区当天的偏移），默认现在。
 */
export function utcClockToLocal(utcHHMM: string, options: DateFormatOptions & { on?: DateValue } = {}): string {
  const match = HHMM.exec(utcHHMM);
  if (!match) throw new RangeError(`Expected HH:MM, got ${utcHHMM}`);
  const on = toDate(options.on ?? Date.now());
  const at = Date.UTC(on.getUTCFullYear(), on.getUTCMonth(), on.getUTCDate(), Number(match[1]), Number(match[2]));
  return formatTime(at, options);
}

/** 平台日偏移（分钟，默认 UTC+9 = 540）→ 平台日起点在 UTC 的 'HH:MM'。 */
export function platformDayStartUtc(offsetMinutes: number): string {
  if (!Number.isInteger(offsetMinutes) || offsetMinutes < -720 || offsetMinutes > 840) {
    throw new RangeError(`offsetMinutes must be an integer in [-720, 840], got ${String(offsetMinutes)}`);
  }
  const minutes = (((-offsetMinutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

export interface LocaleContextValue {
  lang: Lang;
  /** 不传为设备时区（用户端）；后台传 UTC+9 的时区 */
  timeZone?: string | undefined;
}

const LocaleContext = createContext<LocaleContextValue>({ lang: 'en' });

/** 由应用按当前语言提供（用户端不传 timeZone，即设备时区）。 */
export function LocaleProvider({ value, children }: { value: LocaleContextValue; children: ReactNode }) {
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export interface DateFormatter {
  lang: Lang;
  timeZone: string | undefined;
  date(value: DateValue): string;
  time(value: DateValue, seconds?: boolean): string;
  dateTime(value: DateValue, seconds?: boolean): string;
  clock(value: DateValue, now?: DateValue): string;
  /** UTC 'HH:MM' → 本地 'HH:mm' */
  utcClock(utcHHMM: string): string;
}

/** 当前语言与时区下的日期格式化函数。 */
export function useDateFormat(): DateFormatter {
  const { lang, timeZone } = useContext(LocaleContext);
  return useMemo<DateFormatter>(
    () => ({
      lang,
      timeZone,
      date: (v) => formatDate(lang, v, { timeZone }),
      time: (v, seconds) => formatTime(v, { timeZone, seconds }),
      dateTime: (v, seconds) => formatDateTime(lang, v, { timeZone, seconds }),
      clock: (v, now) => formatClock(lang, v, now, { timeZone }),
      utcClock: (hhmm) => utcClockToLocal(hhmm, { timeZone }),
    }),
    [lang, timeZone],
  );
}

export interface DateTimeProps {
  value: DateValue;
  /** date 日期；time 时刻；dateTime 日期 + 时刻（默认）；clock 同日只显示时刻 */
  kind?: 'date' | 'time' | 'dateTime' | 'clock';
  seconds?: boolean;
  className?: string;
}

/** <time> 元素，datetime 属性为 ISO 时间，文字按语言与时区格式化。 */
export function DateTime({ value, kind = 'dateTime', seconds, className }: DateTimeProps) {
  const f = useDateFormat();
  const date = toDate(value);
  const text = kind === 'date' ? f.date(date) : kind === 'time' ? f.time(date, seconds) : kind === 'clock' ? f.clock(date) : f.dateTime(date, seconds);
  return (
    <time dateTime={date.toISOString()} className={className ? `num ${className}` : 'num'}>
      {text}
    </time>
  );
}
